import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { gridQuery } from "./api";
import { messageFor } from "./format";
import {
  baseWeekNote, bandTone, hoursAgo, isKpiUnknown, KPI_COPY, kpiMoveTone, kpiName, kpiQuery, monthLabel, MOVE_VERDICT, moveArrow, moveLabel, NORTH_STAR_IDS,
  NORTH_STAR_TAB, notCountedText, NS_SCOPE_LABEL, NS_SCOPES, nsParamsFrom, scopeKey, unitOf, valueLabel, type Band, type ForecastScope,
  type KpiRead, type Orientation,
} from "./kpi";
import { NorthStarChart, pointNotes } from "./NorthStarChart";
import { RetryAlert, SectionHeading } from "./WeeklyMovesSection";

const T = KPI_COPY.health;

/** A band as the v25 chip: the dot carries the house colour, the text stays ink; a null band is the grey "no band" chip. */
export function BandChip({ band }: { band: Band | null }) {
  return (
    <Badge variant="outline" className="gap-1 text-ks-ink" style={{ borderColor: bandTone(band).solid }}>
      <span aria-hidden className="inline-block size-2 rounded-sm" style={{ background: bandTone(band).solid }} />{band ?? "no band"}
    </Badge>
  );
}

/** A move as v26 shows it: "{prefix} · ↑ +0.1 pts" with a dot toned by whether the move is good or bad for the KPI (C1 v1.30),
 * and the same verdict in words for a screen reader, which never sees the dot. */
export function MoveNote({ prefix, delta, unit, orientation }: { prefix: string; delta: string | null; unit: string; orientation: Orientation | null }) {
  if (delta == null) return <>{prefix}</>;
  const tone = kpiMoveTone(delta, orientation);
  const arrow = moveArrow(delta);
  return (
    <>{prefix}{prefix ? " · " : ""}<span className={tone === "warn" ? "text-ks-warn" : tone === "pass" ? "text-ks-pass" : undefined} data-tone={tone ?? "flat"}>
      {tone && <span aria-hidden className="mr-0.5 inline-block size-1.5 rounded-full bg-current align-middle" />}{arrow ? `${arrow} ` : ""}{moveLabel(delta, unit)}{tone && <span className="sr-only"> {MOVE_VERDICT[tone]}</span>}</span></>
  );
}

/**
 * Section 1 of the Overview (R5 slice 2, CL-041): the North Star chart from C2 v1.11's KPI read, the KPI and scope toggles in
 * the URL, the last-month and forecast tiles beside it. Everything shown is the API's string or band; the browser plots and
 * never computes. The KPI history and bands are the KPI system's; the forecast is this module's own and is labelled so.
 * C1 v1.30: the forecast is the committed one (prices already live) with the staged batch beside it (C2 v1.33), dashed.
 */
export function MarginHealthSection({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const set = (next: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...next }, replace: true });
  const params = nsParamsFrom(search);
  const query = useQuery(kpiQuery(params.kpi));
  // The KPI feed's freshness rides on the grid envelope the shell already holds (one cache entry, the EU grid).
  const envelope = useQuery(gridQuery("EU")).data?.envelope;
  const heading = <SectionHeading n={1} title={T.title} desc={T.desc} />;
  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label={T.kpi} className="flex gap-1">
        {NORTH_STAR_IDS.map((id) => <Button key={id} size="sm" variant={id === params.kpi ? "default" : "outline"} aria-pressed={id === params.kpi} data-nonmutating onClick={() => set({ pp_ns: id === "k125" ? undefined : id })}>{NORTH_STAR_TAB[id]}</Button>)}
      </div>
      <div role="group" aria-label={T.scope} className="flex gap-1">
        {NS_SCOPES.map((s) => <Button key={s} size="sm" variant={s === params.scope ? "default" : "outline"} aria-pressed={s === params.scope} data-nonmutating onClick={() => set({ pp_ns_region: s === "all" ? undefined : s })}>{NS_SCOPE_LABEL[s]}</Button>)}
      </div>
    </div>
  );
  if (query.isPending) {
    return <section className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{toolbar}<Skeleton className="h-56 w-full" /></CardContent></Card></section>;
  }
  if (query.error) {
    const body = isKpiUnknown(query.error)
      ? <Card className="border-dashed"><CardContent className="py-4 text-sm text-ks-ink">{T.notRun}</CardContent></Card>
      : <RetryAlert message={messageFor(query.error, areaName)} onRetry={() => void query.refetch()} />;
    return <section className="flex flex-col gap-3">{heading}{body}</section>;
  }
  const read: KpiRead = query.data;
  const key = scopeKey(params.scope);
  const scope: ForecastScope | null = read.forecast.scopes[key] ?? null;
  const noHistory = read.latest == null;
  const kpiFeed = envelope?.feeds.kpi;
  const label = T.chartLabel(kpiName(read.kpi));
  const baseNote = baseWeekNote(read, scope);
  return (
    <section className="flex flex-col gap-3">
      {heading}
      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <div className="flex flex-wrap items-center gap-3">
            {toolbar}
            <span className="ml-auto flex flex-wrap items-center gap-2 text-xs text-ks-grey-1">
              <span className="font-semibold text-ks-ink">{kpiName(read.kpi)}</span>
              {read.kpi.status && read.kpi.status !== "LIVE" && <Badge variant="outline">{T.status(read.kpi.status)}</Badge>}
              {read.no_target && <Badge variant="outline" className="border-ks-warn text-ks-warn">{read.no_target}</Badge>}
              {kpiFeed && (kpiFeed.latest
                ? <Badge variant="outline" className={kpiFeed.stale ? "border-ks-warn text-ks-warn" : undefined} title={kpiFeed.stale ? T.kpiReadStale : T.kpiReadFresh}>{T.kpiRead(hoursAgo(kpiFeed.latest, new Date()))}</Badge>
                : <Badge variant="outline" className="border-ks-warn text-ks-warn" title={T.kpiReadStale}>{T.kpiReadNoDate}</Badge>)}
            </span>
          </div>
          <p className="text-xs text-ks-grey-1" data-kpi-note>{T.kpiNote(read.kpi.id, read.kpi.co, read.orientation)}</p>
          <p className="text-xs text-ks-grey-1">{read.forecast_label}</p>
          {noHistory && key === "all" && <Alert role="status"><AlertDescription>{T.noHistory}</AlertDescription></Alert>}
          <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
            <div className="flex flex-col gap-2">
              <NorthStarChart read={read} scope={scope} label={label} />
              <ul className="sr-only">{pointNotes(read, scope).map((note) => <li key={note}>{note}</li>)}</ul>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ks-grey-1">
                <li className="flex items-center gap-1"><span aria-hidden className="inline-block h-0.5 w-4 bg-ks-purple" />{T.legendHistory}</li>
                <li className="flex items-center gap-1"><span aria-hidden className="inline-block size-2 rounded-full bg-ks-purple" />{T.legendForecast}</li>
                {scope?.staged?.value != null && <li className="flex items-center gap-1"><span aria-hidden className="inline-block w-3 border-t-2 border-dashed border-ks-purple" /><span aria-hidden className="inline-block size-2 rotate-45 border-2 border-ks-purple" />{T.legendStaged}</li>}
                {read.bands != null && read.bands.length > 0 && <li>{T.legendBands}</li>}
              </ul>
              {scope?.staged?.value != null && <p className="text-xs text-ks-grey-1">{T.stagedFootnote(scope.staged.saved)}</p>}
              {scope?.staged?.value != null && read.staged_label && <p className="text-xs text-ks-grey-1" data-staged-label>{read.staged_label}</p>}
              {scope?.staged && Object.keys(scope.staged.not_counted).length > 0 && <p className="text-xs text-ks-grey-1">{T.notInForecast(notCountedText(scope.staged.not_counted))}</p>}
              <p className="text-xs text-ks-grey-1">{read.history_label}</p>
              {key !== "all" && <p className="text-xs text-ks-grey-1">{read.region_label}</p>}
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-semibold text-ks-ink" data-side-head>{kpiName(read.kpi)}</div>
              <Tile label={T.lastMonth} value={read.latest?.value != null ? `${read.latest.value}%` : null} band={read.latest?.band ?? null}
                note={read.latest ? `${monthLabel(read.latest.period)}, ${T.fromHistory}` : T.noHistoryPoint} />
              <ForecastTile read={read} scope={scope} regionLabel={key === "all" ? null : NS_SCOPE_LABEL[params.scope]} />
              {scope?.value != null && scope.staged && <StagedTile read={read} scope={scope} />}
              {baseNote && <p className="text-xs text-ks-grey-1" data-base-week>{baseNote}</p>}
              <p className="text-xs text-ks-grey-1">{T.sideNote}</p>
              {read.forecast.units && read.forecast.units.unmatched > 0 && <p className="text-xs text-ks-grey-1">{T.unmatched(read.forecast.units.unmatched)}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Tile({ label, value, band, note, accent = false, dashed = false }: { label: string; value: string | null; band: Band | null; note: ReactNode; accent?: boolean; dashed?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${accent ? "border-ks-purple" : "border-ks-grey-3"}${dashed ? " border-dashed" : ""}`}>
      <div className="text-xs text-ks-grey-1">{label}</div>
      {value != null ? <><div className="text-2xl font-semibold tabular-nums text-ks-ink">{value}</div><BandChip band={band} /></> : <div className="text-sm text-ks-ink">{note}</div>}
      {value != null && <div className="mt-1 text-xs text-ks-grey-1">{note}</div>}
    </div>
  );
}

/** The forecast tile: the scope's value and band with "prior {band} · {delta} pts", or why there is no figure, in C2's words. */
function ForecastTile({ read, scope, regionLabel }: { read: KpiRead; scope: ForecastScope | null; regionLabel: string | null }) {
  const label = `${regionLabel ? T.regionForecast(regionLabel) : T.forecast} · ${monthLabel(read.forecast.month)}`;
  if (read.forecast.value == null && read.forecast.scopes.all == null) {
    return <Tile label={label} value={null} band={null} note={T.noCalculationWhy(read.forecast.missing)} accent />;
  }
  if (scope == null || scope.value == null) {
    // C1 v1.31 (Q20): a base week with no weekly margin row is the cause, never today's grids.
    if (scope?.missing.includes("no_base_week_rows")) return <Tile label={label} value={null} band={null} note={T.noCalculationWhy(["no_base_week_rows"])} accent />;
    const noPriced = scope == null || scope.missing.includes("no_priced_rows");
    return <Tile label={label} value={null} band={null} note={noPriced ? (regionLabel ? T.noPriced : T.noPricedAll) : T.noCalculationWhy(scope.missing)} accent />;
  }
  const note = scope.prior
    ? <MoveNote prefix={`prior ${scope.prior.band ?? "no band"}`} delta={scope.delta_pts} unit={unitOf(scope)} orientation={read.orientation} />
    : T.noHistoryPoint;
  return <Tile label={label} value={`${scope.value}%`} band={scope.band} note={note} accent />;
}

/** The staged tile (C1 v1.30): what the staged batch does to the scope's committed figure, C2's figure and band with the move
 * against committed; "Nothing staged" when no saved line is in the scope; C2's reason when the batch has no figure. */
function StagedTile({ read, scope }: { read: KpiRead; scope: ForecastScope }) {
  const staged = scope.staged!;
  if (staged.saved === 0) {
    return <div className="rounded-md border border-dashed border-ks-grey-3 p-3"><Badge variant="outline">{T.nothingStaged}</Badge></div>;
  }
  const label = `${T.staged(staged.saved)} · ${monthLabel(read.forecast.month)}`;
  if (staged.value == null) return <Tile label={label} value={null} band={null} note={T.noStagedFigure(staged.missing)} dashed />;
  const note = <>
    <MoveNote prefix={T.stagedMove(scope.band)} delta={staged.delta} unit={unitOf(scope)} orientation={read.orientation} />
    {staged.lines !== staged.saved && <><br />{T.stagedLines(staged.lines, staged.saved)}</>}
  </>;
  return <Tile label={label} value={valueLabel(staged.value, unitOf(scope))} band={staged.band} note={note} dashed />;
}
